(function (root, factory) {
  const api = factory(root);

  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
  }

  root.TgearTheme = api;

  if (typeof root.document !== "undefined") {
    const boot = () => api.init(root.document);

    if (root.document.readyState === "loading") {
      root.document.addEventListener("DOMContentLoaded", boot, { once: true });
    } else {
      boot();
    }
  }
})(typeof globalThis !== "undefined" ? globalThis : this, function (root) {
  const formatMoney = (cents, currencyCode) => {
    const amount = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currencyCode || "USD"
      }).format(amount);
    } catch (_error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const findMatchingVariant = (variants, selectedOptions) =>
    (variants || []).find((variant) => {
      if (!Array.isArray(variant.options) || variant.options.length !== selectedOptions.length) {
        return false;
      }

      return variant.options.every(
        (optionValue, index) => String(optionValue) === String(selectedOptions[index])
      );
    });

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const initHeader = (doc) => {
    doc.querySelectorAll("[data-sticky-header]").forEach((header) => {
      const isSticky = header.classList.contains("is-sticky");
      const drawer = header.querySelector("[data-header-drawer]");
      const toggleButtons = Array.from(header.querySelectorAll("[data-header-drawer-toggle]"));
      const closeButtons = Array.from(header.querySelectorAll("[data-header-drawer-close]"));
      const announcement = doc.querySelector(".announcement-bar");

      const syncStickyState = () => {
        if (!isSticky) {
          header.classList.remove("is-scrolled");
          return;
        }

        const threshold = Math.max(announcement?.offsetHeight || 0, 16);
        header.classList.toggle("is-scrolled", (root.scrollY || root.pageYOffset || 0) > threshold);
      };

      const closeDrawer = () => {
        if (!drawer || drawer.hidden) return;

        drawer.hidden = true;
        doc.body?.classList.remove("header-drawer-open");
        toggleButtons.forEach((button) => button.setAttribute("aria-expanded", "false"));
      };

      const openDrawer = () => {
        if (!drawer) return;

        drawer.hidden = false;
        doc.body?.classList.add("header-drawer-open");
        toggleButtons.forEach((button) => button.setAttribute("aria-expanded", "true"));
      };

      toggleButtons.forEach((button) => {
        button.addEventListener("click", () => {
          if (drawer?.hidden === false) {
            closeDrawer();
          } else {
            openDrawer();
          }
        });
      });

      closeButtons.forEach((button) => {
        button.addEventListener("click", closeDrawer);
      });

      doc.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          closeDrawer();
        }
      });

      if (typeof root.matchMedia === "function") {
        const mediaQuery = root.matchMedia("(min-width: 990px)");
        const handleChange = (event) => {
          if (event.matches) {
            closeDrawer();
          }
        };

        mediaQuery.addEventListener?.("change", handleChange);
      }

      syncStickyState();
      root.addEventListener("scroll", syncStickyState, { passive: true });
      root.addEventListener("resize", syncStickyState);
    });
  };

  const initAnnouncementBar = (doc) => {
    doc.querySelectorAll("[data-announcement-slider]").forEach((slider) => {
      const slides = Array.from(slider.querySelectorAll("[data-announcement-slide]"));
      const prev = slider.querySelector("[data-announcement-prev]");
      const next = slider.querySelector("[data-announcement-next]");
      const counter = slider.querySelector("[data-announcement-counter]");
      const autoplayMs = Number(slider.dataset.autoplayMs) || 5000;
      let index = 0;
      let autoplayId;

      if (!slides.length) return;

      if (slides.length < 2) {
        if (prev) prev.hidden = true;
        if (next) next.hidden = true;
      }

      const update = () => {
        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle("is-active", slideIndex === index);
        });

        if (counter) {
          counter.textContent = `${index + 1}/${slides.length}`;
        }
      };

      const restartAutoplay = () => {
        if (autoplayId) {
          root.clearInterval(autoplayId);
        }

        if (slides.length < 2) return;

        autoplayId = root.setInterval(() => {
          index = (index + 1) % slides.length;
          update();
        }, autoplayMs);
      };

      const activate = (nextIndex) => {
        index = (nextIndex + slides.length) % slides.length;
        update();
        restartAutoplay();
      };

      prev?.addEventListener("click", () => activate(index - 1));
      next?.addEventListener("click", () => activate(index + 1));

      update();
      restartAutoplay();
    });
  };

  const initHero = (doc) => {
    const section = doc.querySelector("[data-hero-slider]");
    if (!section) return;

    const slides = Array.from(section.querySelectorAll("[data-slide]"));
    const counter = section.querySelector("[data-counter]");
    const counterLabel = section.querySelector("[data-counter-label]");
    const prev = section.querySelector("[data-prev]");
    const next = section.querySelector("[data-next]");
    const autoplayMs = Number(section.dataset.autoplayMs) || 5000;
    let index = 0;
    let autoplayId;
    let progressFrame;

    if (!slides.length) return;

    const updateCounter = () => {
      const label = `${index + 1}/${slides.length}`;

      if (counterLabel) {
        counterLabel.textContent = label;
      } else if (counter) {
        counter.textContent = label;
      }
    };

    const stopProgress = () => {
      if (progressFrame && typeof root.cancelAnimationFrame === "function") {
        root.cancelAnimationFrame(progressFrame);
      }
      progressFrame = undefined;
    };

    const startProgress = () => {
      if (!counter) return;

      stopProgress();
      counter.style.setProperty("--hero-progress", "0");

      if (typeof root.requestAnimationFrame !== "function") {
        counter.style.setProperty("--hero-progress", "100");
        return;
      }

      const startedAt = root.performance?.now?.() ?? Date.now();

      const tick = (now) => {
        const timestamp = typeof now === "number" ? now : Date.now();
        const progress = Math.min((timestamp - startedAt) / autoplayMs, 1) * 100;

        counter.style.setProperty("--hero-progress", progress.toFixed(2));

        if (progress < 100) {
          progressFrame = root.requestAnimationFrame(tick);
        }
      };

      progressFrame = root.requestAnimationFrame(tick);
    };

    const syncAutoplay = () => {
      if (autoplayId) {
        root.clearInterval(autoplayId);
      }

      if (slides.length < 2) {
        if (counter) counter.style.setProperty("--hero-progress", "100");
        return;
      }

      startProgress();
      autoplayId = root.setInterval(() => activate(index + 1), autoplayMs);
    };

    const activate = (nextIndex, options = {}) => {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) =>
        slide.classList.toggle("is-active", slideIndex === index)
      );

      updateCounter();

      if (options.skipAutoplay !== true) {
        syncAutoplay();
      }
    };

    prev?.addEventListener("click", () => activate(index - 1));
    next?.addEventListener("click", () => activate(index + 1));

    activate(0, { skipAutoplay: true });
    syncAutoplay();
  };

  const initCountdown = (doc) => {
    doc.querySelectorAll("[data-countdown]").forEach((node) => {
      const end = new Date(node.dataset.countdown).getTime();
      if (Number.isNaN(end)) return;

      const tick = () => {
        const now = Date.now();
        const diff = end - now;

        if (diff <= 0) {
          node.textContent = "00d 00h 00m";
          return;
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((diff / (1000 * 60)) % 60);
        node.textContent = `${String(days).padStart(2, "0")}d ${String(hours).padStart(2, "0")}h ${String(minutes).padStart(2, "0")}m`;
      };

      tick();
      root.setInterval(tick, 60000);
    });
  };

  const initHotspots = (doc) => {
    doc.querySelectorAll("[data-hotspot]").forEach((wrap) => {
      const popup = wrap.querySelector("[data-hotspot-popup]");
      const title = popup?.querySelector("[data-popup-title]");
      const text = popup?.querySelector("[data-popup-text]");
      const coordinates = wrap.querySelector("[data-hotspot-coordinates]");
      const isDesignMode = Boolean(root.Shopify && root.Shopify.designMode);

      const positionPopup = (dot) => {
        if (!popup || !title || !text) return;

        title.textContent = dot.dataset.title || "";
        text.textContent = dot.dataset.text || "";

        const dotX = Number(dot.dataset.x || 50);
        const dotY = Number(dot.dataset.y || 50);

        if (dotX > 50) {
          popup.style.left = "auto";
          popup.style.right = `${100 - dotX + 3}%`;
        } else {
          popup.style.right = "auto";
          popup.style.left = `${dotX + 3}%`;
        }

        if (dotY > 70) {
          popup.style.top = "auto";
          popup.style.bottom = `${100 - dotY + 3}%`;
        } else {
          popup.style.bottom = "auto";
          popup.style.top = `${dotY}%`;
        }

        popup.hidden = false;
      };

      wrap.querySelectorAll("[data-hotspot-dot]").forEach((dot) => {
        let movedDuringDrag = false;

        const updateDotPosition = (event) => {
          const rect = wrap.getBoundingClientRect();
          const x = clamp(((event.clientX - rect.left) / rect.width) * 100, 2, 98);
          const y = clamp(((event.clientY - rect.top) / rect.height) * 100, 2, 98);

          dot.style.left = `${x}%`;
          dot.style.top = `${y}%`;
          dot.dataset.x = String(Math.round(x));
          dot.dataset.y = String(Math.round(y));

          if (coordinates) {
            coordinates.hidden = false;
            coordinates.textContent = `X ${Math.round(x)} / Y ${Math.round(y)} - save in block settings`;
          }
        };

        if (isDesignMode) {
          dot.addEventListener("pointerdown", (event) => {
            movedDuringDrag = false;
            dot.dataset.dragging = "true";
            dot.setPointerCapture?.(event.pointerId);
          });

          dot.addEventListener("pointermove", (event) => {
            if (dot.dataset.dragging !== "true") return;

            movedDuringDrag = true;
            updateDotPosition(event);
          });

          const endDrag = () => {
            if (dot.dataset.dragging !== "true") return;

            delete dot.dataset.dragging;

            if (coordinates && movedDuringDrag) {
              root.setTimeout(() => {
                coordinates.hidden = true;
              }, 2000);
            }
          };

          dot.addEventListener("pointerup", endDrag);
          dot.addEventListener("pointercancel", endDrag);
        }

        dot.addEventListener("click", (event) => {
          if (movedDuringDrag) {
            movedDuringDrag = false;
            event.preventDefault();
            return;
          }

          positionPopup(dot);
        });
      });

      wrap.querySelector("[data-close]")?.addEventListener("click", () => {
        if (popup) popup.hidden = true;
      });
    });
  };

  const initCarousels = (doc) => {
    doc.querySelectorAll("[data-carousel-shell]").forEach((shell) => {
      const carousel = shell.querySelector("[data-carousel]");
      const prev = shell.querySelector("[data-carousel-prev]");
      const next = shell.querySelector("[data-carousel-next]");
      const items = Array.from(carousel?.querySelectorAll(".carousel-item") || []);
      let queuedFrame;

      if (!carousel || !items.length) {
        if (prev) prev.hidden = true;
        if (next) next.hidden = true;
        return;
      }

      const getGap = () => {
        const styles = typeof root.getComputedStyle === "function" ? root.getComputedStyle(carousel) : null;
        return Number.parseFloat(styles?.columnGap || styles?.gap || "0");
      };

      const getStep = () => {
        const firstItem = items[0];
        const gap = getGap();

        return (firstItem?.getBoundingClientRect().width || Math.max(carousel.clientWidth * 0.82, 260)) + gap;
      };

      const getMaxIndex = () => {
        const step = getStep();
        const gap = getGap();

        if (!step) return 0;

        const visibleItems = Math.max(1, Math.floor((carousel.clientWidth + gap) / step));
        return Math.max(0, items.length - visibleItems);
      };

      const getCurrentIndex = () => {
        const step = getStep();

        if (!step) return 0;

        return clamp(Math.round(carousel.scrollLeft / step), 0, getMaxIndex());
      };

      const updateNavState = (index = getCurrentIndex()) => {
        const maxIndex = getMaxIndex();

        if (prev) prev.hidden = index <= 0;
        if (next) next.hidden = index >= maxIndex;
      };

      const scrollToIndex = (nextIndex) => {
        const targetIndex = clamp(nextIndex, 0, getMaxIndex());

        carousel.scrollTo({ left: targetIndex * getStep(), behavior: "smooth" });
        updateNavState(targetIndex);
      };

      prev?.addEventListener("click", () => {
        scrollToIndex(getCurrentIndex() - 1);
      });

      next?.addEventListener("click", () => {
        scrollToIndex(getCurrentIndex() + 1);
      });

      carousel.addEventListener(
        "scroll",
        () => {
          if (queuedFrame) return;

          queuedFrame = root.requestAnimationFrame?.(() => {
            queuedFrame = undefined;
            updateNavState();
          });

          if (!queuedFrame) {
            updateNavState();
          }
        },
        { passive: true }
      );

      root.addEventListener?.("resize", () => updateNavState());
      updateNavState();
    });
  };

  const initFaqAccordion = (doc) => {
    doc.querySelectorAll("[data-faq]").forEach((accordion) => {
      const items = Array.from(accordion.querySelectorAll("[data-faq-item]"));

      const closeItem = (item) => {
        const trigger = item.querySelector("[data-faq-trigger]");
        const panel = item.querySelector("[data-faq-panel]");

        if (!trigger || !panel) return;

        item.classList.remove("is-open");
        trigger.setAttribute("aria-expanded", "false");
        panel.classList.remove("is-open");

        root.setTimeout(() => {
          if (!item.classList.contains("is-open")) {
            panel.hidden = true;
          }
        }, 350);
      };

      const openItem = (item) => {
        const trigger = item.querySelector("[data-faq-trigger]");
        const panel = item.querySelector("[data-faq-panel]");

        if (!trigger || !panel) return;

        item.classList.add("is-open");
        trigger.setAttribute("aria-expanded", "true");
        panel.hidden = false;

        root.requestAnimationFrame?.(() => {
          panel.classList.add("is-open");
        });

        if (typeof root.requestAnimationFrame !== "function") {
          panel.classList.add("is-open");
        }
      };

      items.forEach((item) => {
        const trigger = item.querySelector("[data-faq-trigger]");
        const panel = item.querySelector("[data-faq-panel]");
        const isInitiallyOpen = item.classList.contains("is-open");

        if (panel) {
          panel.hidden = !isInitiallyOpen;
          panel.classList.toggle("is-open", isInitiallyOpen);
        }

        trigger?.addEventListener("click", () => {
          const isOpen = item.classList.contains("is-open");

          items.forEach((otherItem) => {
            if (otherItem !== item) {
              closeItem(otherItem);
            }
          });

          if (isOpen) {
            closeItem(item);
          } else {
            openItem(item);
          }
        });
      });
    });
  };

  const deriveInstagramUsername = (profileUrl) => {
    try {
      const pathname = new URL(profileUrl).pathname.replace(/^\/+|\/+$/g, "");
      return pathname.split("/")[0] || "";
    } catch (_error) {
      return "";
    }
  };

  const normalizeInstagramPosts = (payload) => {
    const customItems =
      payload?.items ||
      payload?.data?.items ||
      payload?.posts;

    if (Array.isArray(customItems)) {
      return customItems
        .map((item) => ({
          link: item.link || item.permalink || item.url,
          thumbnail: item.thumbnail || item.thumbnail_url || item.image || item.media_url,
          isVideo: Boolean(item.is_video || item.media_type === "VIDEO" || item.type === "video"),
          alt: item.caption || item.alt || "Instagram post"
        }))
        .filter((item) => item.link && item.thumbnail);
    }

    const edges =
      payload?.data?.user?.edge_owner_to_timeline_media?.edges ||
      payload?.graphql?.user?.edge_owner_to_timeline_media?.edges ||
      [];

    return edges
      .map((edge) => {
        const node = edge?.node || {};
        const shortcode = node.shortcode;

        return {
          link: shortcode ? `https://www.instagram.com/p/${shortcode}/` : "",
          thumbnail: node.display_url || node.thumbnail_src,
          isVideo: Boolean(node.is_video),
          alt: node.accessibility_caption || "Instagram post"
        };
      })
      .filter((item) => item.link && item.thumbnail);
  };

  const createInstagramCard = (doc, post) => {
    const anchor = doc.createElement("a");
    anchor.className = "ig-card";
    anchor.href = post.link;
    anchor.target = "_blank";
    anchor.rel = "noopener";

    const image = doc.createElement("img");
    image.src = post.thumbnail;
    image.alt = post.alt || "Instagram post";
    image.loading = "lazy";
    anchor.appendChild(image);

    const icon = doc.createElement("span");
    icon.className = "ig-icon";
    icon.innerHTML = post.isVideo
      ? '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#fff" stroke-width="2"><path d="M8 6v12l10-6-10-6z" fill="#fff" stroke="none"/></svg>'
      : '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#fff" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5" fill="#fff" stroke="none"/><path d="M21 15l-5-5L5 21"/></svg>';
    anchor.appendChild(icon);

    return anchor;
  };

  const initInstagramFeed = (doc) => {
    doc.querySelectorAll("[data-instagram-feed]").forEach(async (section) => {
      const grid = section.querySelector("[data-instagram-grid]");
      const empty = section.querySelector("[data-instagram-empty]");
      const profileUrl = section.dataset.profileUrl;
      const feedUrl = section.dataset.feedUrl;
      const limit = Number(section.dataset.feedLimit) || 7;
      const manualPosts = Array.from(section.querySelectorAll("[data-manual-post]"));

      if (!grid || !profileUrl || typeof root.fetch !== "function") return;

      const fetchJson = async (url, options = {}) => {
        const response = await root.fetch(url, options);
        if (!response.ok) {
          throw new Error(`Feed request failed: ${response.status}`);
        }
        return response.json();
      };

      try {
        let posts = [];

        if (feedUrl) {
          posts = normalizeInstagramPosts(await fetchJson(feedUrl));
        }

        if (!posts.length) {
          const username = deriveInstagramUsername(profileUrl);

          if (username) {
            posts = normalizeInstagramPosts(
              await fetchJson(
                `https://www.instagram.com/api/v1/users/web_profile_info/?username=${encodeURIComponent(username)}`,
                {
                  headers: {
                    "x-ig-app-id": "936619743392459"
                  }
                }
              )
            );
          }
        }

        if (posts.length) {
          grid.replaceChildren(...posts.slice(0, limit).map((post) => createInstagramCard(doc, post)));
          if (empty) empty.hidden = true;
          return;
        }
      } catch (_error) {
        // Leave manual blocks in place when auto-fetch is not available.
      }

      if (manualPosts.length === 0 && empty) {
        empty.hidden = false;
      }
    });
  };

  const initSearchDrawer = (doc) => {
    const drawer = doc.querySelector("[data-search-drawer]");
    if (!drawer) return;

    const toggles = Array.from(doc.querySelectorAll("[data-search-toggle]"));
    const closeButtons = Array.from(drawer.querySelectorAll("[data-search-close]"));
    const input = drawer.querySelector(".search-drawer__input");

    const open = () => {
      drawer.classList.add("is-open");
      doc.body?.classList.add("side-drawer-open");
      root.setTimeout(() => input?.focus(), 150);
    };

    const close = () => {
      drawer.classList.remove("is-open");
      doc.body?.classList.remove("side-drawer-open");
    };

    toggles.forEach((btn) => btn.addEventListener("click", open));
    closeButtons.forEach((btn) => btn.addEventListener("click", close));

    doc.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && drawer.classList.contains("is-open")) {
        close();
      }
    });
  };

  const initCartDrawer = (doc) => {
    const drawer = doc.querySelector("[data-cart-drawer]");
    if (!drawer) return;

    const toggles = Array.from(doc.querySelectorAll("[data-cart-toggle]"));
    const closeButtons = Array.from(drawer.querySelectorAll("[data-cart-close]"));

    const open = () => {
      drawer.classList.add("is-open");
      doc.body?.classList.add("side-drawer-open");
    };

    const close = () => {
      drawer.classList.remove("is-open");
      doc.body?.classList.remove("side-drawer-open");
    };

    toggles.forEach((btn) => btn.addEventListener("click", open));
    closeButtons.forEach((btn) => btn.addEventListener("click", close));

    doc.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && drawer.classList.contains("is-open")) {
        close();
      }
    });

    const getCurrencyCode = () => {
      const root = doc.querySelector("[data-product-root]");
      return root?.dataset.currencyCode || "USD";
    };

    const renderLinePrice = (item, currencyCode) => {
      const finalPrice = formatMoney(item.final_line_price, currencyCode);
      if (item.original_line_price && item.original_line_price !== item.final_line_price) {
        return `<span class="cart-drawer__item-price-current">${finalPrice}</span><s class="cart-drawer__item-price-original">${formatMoney(item.original_line_price, currencyCode)}</s>`;
      }
      return `<span class="cart-drawer__item-price-current">${finalPrice}</span>`;
    };

    const renderCartDrawer = (cart) => {
      const itemsWrap = drawer.querySelector("[data-cart-items]");
      const emptyWrap = drawer.querySelector("[data-cart-empty]");
      const footer = drawer.querySelector("[data-cart-footer]");
      const countBadge = doc.querySelector("[data-cart-count]");
      const subtotalEl = drawer.querySelector("[data-cart-subtotal]");
      const currencyCode = getCurrencyCode();

      if (!itemsWrap) return;

      if (cart.item_count === 0) {
        itemsWrap.innerHTML = "";
        itemsWrap.style.display = "none";
        if (emptyWrap) emptyWrap.style.display = "";
        if (footer) footer.style.display = "none";
        if (countBadge) { countBadge.textContent = "0"; countBadge.style.display = "none"; }
        return;
      }

      itemsWrap.style.display = "";
      if (emptyWrap) emptyWrap.style.display = "none";
      if (footer) footer.style.display = "";
      if (countBadge) { countBadge.textContent = String(cart.item_count); countBadge.style.display = ""; }
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price, currencyCode);

      itemsWrap.innerHTML = cart.items.map((item) => {
        const variantTitle = item.variant_title && item.variant_title !== "Default Title"
          ? `<p style="font-size:12px;color:var(--color-muted);margin:2px 0;">${item.variant_title}</p>`
          : "";
        const imgHtml = item.image
          ? `<img src="${item.image}" alt="${item.product_title}" class="cart-drawer__item-image" loading="lazy">`
          : `<div class="cart-drawer__item-image" style="background:var(--color-surface-muted);"></div>`;
        return `<div class="cart-drawer__item" data-cart-item-key="${item.key}">
          ${imgHtml}
          <div>
            <p class="cart-drawer__item-title">${item.product_title}</p>
            ${variantTitle}
            <p class="cart-drawer__item-price">${renderLinePrice(item, currencyCode)}</p>
            <p class="cart-drawer__item-qty">Qty: ${item.quantity}</p>
            <button type="button" class="cart-drawer__item-remove" data-remove-key="${item.key}">Remove</button>
          </div>
        </div>`;
      }).join("");

      bindRemoveButtons();
    };

    const removeItem = async (key) => {
      try {
        await root.fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        const res = await root.fetch("/cart.js");
        const cart = await res.json();
        renderCartDrawer(cart);
      } catch (_e) { /* silent */ }
    };

    const bindRemoveButtons = () => {
      drawer.querySelectorAll("[data-remove-key]").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          const key = btn.getAttribute("data-remove-key");
          const item = btn.closest(".cart-drawer__item");
          if (item) item.style.opacity = "0.4";
          removeItem(key);
        });
      });
    };

    bindRemoveButtons();

    // Expose for use by AJAX add-to-cart
    root.__cartDrawer = { open, close, renderCartDrawer };
  };

  const initSelectDefaults = (doc) => {
    doc.querySelectorAll("select[data-default]").forEach((select) => {
      const defaultValue = select.dataset.default;
      if (!defaultValue) return;

      const matchingOption = Array.from(select.options).find(
        (option) => option.value === defaultValue || option.text === defaultValue
      );

      if (matchingOption) {
        select.value = matchingOption.value;
      }
    });
  };

  const getSelectedOptions = (form) => {
    const names = [];
    const values = {};
    form.querySelectorAll('input[type="radio"][name^="options["]').forEach((input) => {
      const name = input.name;
      if (!names.includes(name)) names.push(name);
      if (input.checked) values[name] = input.value;
    });
    return names.map((name) => values[name] || "");
  };

  const updatePurchaseState = (sectionRoot, variant) => {
    const hiddenInput = sectionRoot.querySelector("[data-variant-id-input]");
    const currencyCode = sectionRoot.dataset.currencyCode || "USD";
    const priceNode = sectionRoot.querySelector("[data-product-price]");
    const bundleSection = sectionRoot.querySelector("[data-bundle-section]");
    const popupPrice = root.document?.querySelector("[data-popup-price]");
    const actionButtons = [
      sectionRoot.querySelector("[data-main-add]"),
      root.document?.querySelector("[data-popup-add]")
    ].filter(Boolean);

    if (!variant || !hiddenInput) return;

    hiddenInput.value = variant.id;

    if (priceNode && !bundleSection) {
      priceNode.textContent = formatMoney(variant.price, currencyCode);
    }

    if (popupPrice) {
      popupPrice.textContent = formatMoney(variant.price, currencyCode);
    }

    actionButtons.forEach((button) => {
      if (!button.dataset.defaultLabel) {
        button.dataset.defaultLabel = button.textContent.trim();
      }

      button.disabled = !variant.available;
      button.classList.toggle("is-disabled", !variant.available);

      if (button.matches("[data-main-add]")) {
        button.textContent = variant.available ? button.dataset.defaultLabel : "Sold out";
      }

      if (button.matches("[data-popup-add]")) {
        button.textContent = variant.available ? button.dataset.defaultLabel : "Sold out";
      }
    });

    /* variant param removed from URL for cleaner links */
  };

  const parsePreviewConfig = (rawValue) => {
    if (!rawValue) return null;
    let parsed = rawValue;
    for (let i = 0; i < 4; i += 1) {
      if (typeof parsed === "string") {
        try {
          parsed = JSON.parse(parsed);
          continue;
        } catch (_error) {
          break;
        }
      }
      if (!parsed || typeof parsed !== "object") break;
      if (parsed.packDiscounts || parsed.crossSellDiscount) return parsed;
      if (typeof parsed.value !== "undefined") {
        parsed = parsed.value;
        continue;
      }
      break;
    }
    return parsed && typeof parsed === "object" ? parsed : null;
  };

  const normalizePreviewRule = (rule) => {
    if (!rule || typeof rule !== "object") return null;
    const type = rule.type === "fixed_amount" ? "fixed_amount" : "percentage";
    const value = Number(rule.value);
    if (!Number.isFinite(value) || value <= 0) return null;
    return { type, value };
  };

  const applyPreviewRule = (totalCents, rule) => {
    const safeTotal = Math.max(0, Math.round(totalCents || 0));
    if (!rule) return safeTotal;
    if (rule.type === "fixed_amount") {
      return Math.max(0, safeTotal - Math.round(rule.value * 100));
    }
    return Math.max(0, Math.round(safeTotal * (1 - rule.value / 100)));
  };

  const getPerItemPreviewPrice = (totalCents, itemCount) => {
    const count = Math.max(1, Number.parseInt(itemCount, 10) || 1);
    return Math.max(0, Math.round(Math.max(0, Math.round(totalCents || 0)) / count));
  };

  const setBundleCardPrice = (node, cents, currencyCode) => {
    if (!node) return;
    const valueNode = node.querySelector(".bundle-price-value, .product-price-value");
    const suffixNode = node.querySelector(".bundle-price-suffix, .product-price-suffix");
    const formatted = formatMoney(cents, currencyCode);
    if (valueNode) {
      valueNode.textContent = formatted;
    } else {
      node.textContent = formatted;
    }
    if (suffixNode) {
      suffixNode.textContent = "/each";
      suffixNode.style.display = "";
    }
  };

  const setBundleMainPrice = (node, cents, currencyCode) => {
    if (!node) return;
    const valueNode = node.querySelector(".product-price-value");
    const suffixNode = node.querySelector(".product-price-suffix");
    const formatted = formatMoney(cents, currencyCode);
    if (valueNode) {
      valueNode.textContent = formatted;
    } else {
      node.textContent = formatted;
    }
    if (suffixNode) {
      suffixNode.textContent = "";
      suffixNode.style.display = "none";
    }
  };

  const formatPreviewRuleLabel = (rule, currencyCode) => {
    if (!rule) return "";
    if (rule.type === "fixed_amount") {
      return `${formatMoney(Math.round(rule.value * 100), currencyCode)} OFF`;
    }
    return `${Math.round(rule.value)}% OFF`;
  };

  const findPreviewVariant = (variants, scent, size) => {
    for (let i = 0; i < variants.length; i += 1) {
      const variant = variants[i];
      let matches = true;
      if (scent && variant.option1 !== scent && variant.option2 !== scent && variant.option3 !== scent) matches = false;
      if (size && variant.option1 !== size && variant.option2 !== size && variant.option3 !== size) matches = false;
      if (matches) return variant;
    }
    return variants[0] || null;
  };

  const fetchFreshBundlePreviewConfig = async (sectionRoot) => {
    const sectionId = sectionRoot.querySelector('input[name="section-id"]')?.value;
    if (!sectionId) {
      return parsePreviewConfig(sectionRoot.querySelector("[data-bundle-discount-config]")?.textContent);
    }

    try {
      const url = new URL(root.location.href);
      url.search = "";
      url.searchParams.set("section_id", sectionId);
      url.searchParams.set("_cb", String(Date.now()));
      const response = await root.fetch(url.toString(), {
        credentials: "same-origin",
        headers: { "Cache-Control": "no-cache" }
      });
      const html = await response.text();
      const parsedDocument = new DOMParser().parseFromString(html, "text/html");
      return parsePreviewConfig(parsedDocument.querySelector("[data-bundle-discount-config]")?.textContent);
    } catch (_error) {
      return parsePreviewConfig(sectionRoot.querySelector("[data-bundle-discount-config]")?.textContent);
    }
  };

  const updateBundlePreviewDisplay = (sectionRoot, variants, rawConfig) => {
    const bundleSection = sectionRoot.querySelector("[data-bundle-section]");
    const priceNode = sectionRoot.querySelector("[data-product-price]");
    const totalNode = bundleSection?.querySelector("[data-bundle-total]");
    const configNode = sectionRoot.querySelector("[data-bundle-discount-config]") || root.document?.querySelector("[data-bundle-discount-config]");
    const currencyCode = sectionRoot.dataset.currencyCode || "USD";
    if (!bundleSection || !priceNode) return;

    const config = parsePreviewConfig(rawConfig);
    const rules = {
      1: null,
      2: normalizePreviewRule(config?.packDiscounts?.["2"]),
      3: normalizePreviewRule(config?.packDiscounts?.["3"])
    };

    sectionRoot.__teloPreviewConfig = config;
    if (configNode) {
      configNode.textContent = config ? JSON.stringify(config) : "null";
    }

    const getTopScent = () => sectionRoot.querySelector("[data-top-scent]:checked")?.value || "";
    const getTopSize = () => sectionRoot.querySelector("[data-top-size]:checked")?.value || "";
    const getCurrentPack = () => Number.parseInt(bundleSection.querySelector("[data-bundle-radio]:checked")?.value || "1", 10) || 1;
    const getPackFullPrice = (pack) => {
      const inline = bundleSection.querySelector(`[data-bundle-items-inline="${pack}"]`);
      const fallbackScent = getTopScent();
      const fallbackSize = getTopSize();
      let total = 0;
      if (!inline) {
        const variant = findPreviewVariant(variants, fallbackScent, fallbackSize);
        return variant ? (variant.price || 0) * Math.max(1, pack) : 0;
      }
      for (let index = 1; index <= pack; index += 1) {
        const scent = inline.querySelector(`[data-bundle-scent="${index}"]`)?.value || fallbackScent;
        const size = inline.querySelector(`[data-bundle-size="${index}"]`)?.value || fallbackSize;
        const variant = findPreviewVariant(variants, scent, size);
        if (variant) total += variant.price || 0;
      }
      return total;
    };

    for (let pack = 1; pack <= 3; pack += 1) {
      const priceEl = bundleSection.querySelector(`[data-bundle-price-${pack}]`);
      const compareEl = bundleSection.querySelector(`[data-bundle-compare-${pack}]`);
      const badgeEl = bundleSection.querySelector(`[data-bundle-badge-${pack}]`);
      const fullPrice = getPackFullPrice(pack);
      const discountedPrice = applyPreviewRule(fullPrice, rules[pack]);
      const eachDiscountedPrice = getPerItemPreviewPrice(discountedPrice, pack);
      const eachFullPrice = getPerItemPreviewPrice(fullPrice, pack);
      const label = pack > 1 ? formatPreviewRuleLabel(rules[pack], currencyCode) : "";

      setBundleCardPrice(priceEl, eachDiscountedPrice, currencyCode);

      if (badgeEl) {
        badgeEl.textContent = label;
        badgeEl.style.display = label ? "inline-block" : "none";
      }

      if (compareEl) {
        compareEl.textContent = formatMoney(eachFullPrice, currencyCode);
        compareEl.style.display = pack > 1 && label ? "block" : "none";
      }
    }

    const activePack = getCurrentPack();
    const fullTotal = getPackFullPrice(activePack);
    const discountedTotal = applyPreviewRule(fullTotal, rules[activePack]);
    if (totalNode) totalNode.textContent = formatMoney(discountedTotal, currencyCode);
    setBundleMainPrice(priceNode, discountedTotal, currencyCode);

    const previewConfig = config && typeof config === "object" ? config : {};
    root.__teloCartPreviewConfig = previewConfig;
    const cartCrosssell = root.document?.querySelector("[data-cart-crosssell]");
    if (cartCrosssell) {
      cartCrosssell.setAttribute("data-cart-preview-config", JSON.stringify(previewConfig));
      const heading = cartCrosssell.querySelector(".cart-crosssell__heading");
      const staticHeading =
        cartCrosssell.getAttribute("data-crosssell-static-heading") ||
        "Add This For 25% OFF";
      if (heading) {
        heading.textContent = staticHeading;
      }
      cartCrosssell.querySelectorAll("[data-crosssell-base-price]").forEach((item) => {
        const basePrice = Number.parseInt(item.getAttribute("data-crosssell-base-price") || "0", 10) || 0;
        const staticDiscountCents =
          Number.parseInt(item.getAttribute("data-crosssell-static-discount-cents") || "0", 10) || 0;
        const discountedPrice = Math.max(0, basePrice - staticDiscountCents);
        const currentPrice = item.querySelector(".cart-crosssell__price-current");
        const originalPrice = item.querySelector(".cart-crosssell__price-original");
        if (currentPrice) currentPrice.textContent = formatMoney(discountedPrice, currencyCode);
        if (originalPrice) {
          originalPrice.textContent = formatMoney(basePrice, currencyCode);
          originalPrice.style.display = discountedPrice < basePrice ? "" : "none";
        }
      });
    }
    root.document?.dispatchEvent(new root.CustomEvent("telo:preview-config-update", {
      detail: previewConfig
    }));
  };

  const initProductForms = (doc) => {
    doc.querySelectorAll("[data-product-root]").forEach((sectionRoot) => {
      const form = sectionRoot.querySelector(".product-form");
      const variantsNode = sectionRoot.querySelector("[data-variants-json]");
      const popup = doc.querySelector("[data-quick-popup]");

      if (!form || !variantsNode) return;

      let variants = [];

      try {
        variants = JSON.parse(variantsNode.textContent || "[]");
      } catch (_error) {
        variants = [];
      }

      // Parse variant→image map
      let variantImages = {};
      try {
        const viNode = sectionRoot.querySelector("[data-variant-images]");
        if (viNode) variantImages = JSON.parse(viNode.textContent || "{}");
      } catch (_e) {
        variantImages = {};
      }

      const mainImgContainer = sectionRoot.querySelector("[data-product-main-image]");
      const mainImg = mainImgContainer?.querySelector("img");

      const swapMainImage = (src) => {
        if (!mainImg || !src) return;
        mainImg.classList.add("is-fading");
        setTimeout(() => {
          mainImg.src = src;
          mainImg.srcset = "";
          mainImg.addEventListener("load", () => mainImg.classList.remove("is-fading"), { once: true });
          if (mainImg.complete) mainImg.classList.remove("is-fading");
        }, 300);
      };

      // Thumbnail click → swap main image
      sectionRoot.querySelectorAll(".product-gallery__thumb").forEach((thumb) => {
        thumb.addEventListener("click", () => {
          const img = thumb.querySelector("img");
          if (!img) return;
          // Use the high-res version from src
          swapMainImage(img.src);
          // Highlight active thumb
          sectionRoot.querySelectorAll(".product-gallery__thumb").forEach((t) => t.classList.remove("is-active"));
          thumb.classList.add("is-active");
        });
      });

      const syncVariant = () => {
        const selectedOptions = getSelectedOptions(form);
        const variant =
          findMatchingVariant(variants, selectedOptions) ||
          variants.find(
            (item) =>
              Number(item.id) === Number(sectionRoot.querySelector("[data-variant-id-input]")?.value)
          ) ||
          variants[0];

        updatePurchaseState(sectionRoot, variant);

        // Swap main image if variant has a featured image
        if (variant && variantImages[String(variant.id)]) {
          swapMainImage(variantImages[String(variant.id)]);
        }
      };

      form.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.addEventListener("change", (e) => {
          syncVariant();
          // Update swatch active states and label
          const swatch = e.target.closest(".option-swatch");
          if (swatch) {
            const list = swatch.closest(".option-swatch-list");
            if (list) list.querySelectorAll(".option-swatch").forEach((s) => s.classList.remove("is-active"));
            swatch.classList.add("is-active");
            const pos = e.target.getAttribute("data-option-position");
            const label = sectionRoot.querySelector('[data-swatch-selected="' + pos + '"]');
            if (label) label.textContent = e.target.value;
          }
        });
      });

      syncVariant();

      const bundleSection = sectionRoot.querySelector("[data-bundle-section]");
      if (bundleSection) {
        // Bundle pricing on this theme is handled by the section's inline script.
        // Keep theme.js out of the way so it doesn't overwrite the product page state.
      }

      // AJAX add-to-cart handler
      const ajaxAddToCart = async (button) => {
        if (button.disabled || button.classList.contains("is-loading")) return;

        const variantInput = sectionRoot.querySelector("[data-variant-id-input]");
        const qtyInput = sectionRoot.querySelector('input[name="quantity"]');
        const variantId = variantInput?.value;
        const quantity = Number(qtyInput?.value) || 1;

        if (!variantId) return;

        const originalText = button.textContent;
        button.classList.add("is-loading");
        button.textContent = "";

        try {
          await root.fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: Number(variantId), quantity })
          });

          const cartRes = await root.fetch("/cart.js");
          const cart = await cartRes.json();

          if (root.__cartDrawer) {
            root.__cartDrawer.renderCartDrawer(cart);
            root.__cartDrawer.open();
          }
        } catch (_e) {
          /* fallback: do nothing special */
        } finally {
          button.classList.remove("is-loading");
          button.textContent = originalText;
        }
      };

      // Intercept form submit → use AJAX
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        const button = form.querySelector("[data-main-add]");
        if (button) ajaxAddToCart(button);
      });

      // Sync popup options to main form options
      const syncPopupToMain = () => {
        if (!popup) return;
        popup.querySelectorAll("[data-popup-option]").forEach((popupInput) => {
          if (!popupInput.checked) return;
          const pos = popupInput.getAttribute("data-option-position");
          const val = popupInput.value;
          form.querySelectorAll('input[type="radio"]').forEach((mainInput) => {
            if (mainInput.value === val) {
              mainInput.checked = true;
            }
          });
        });
        syncVariant();
        // Update popup price
        const priceEl = popup.querySelector("[data-popup-price]");
        const mainPrice = sectionRoot.querySelector("[data-product-price]");
        if (priceEl && mainPrice) priceEl.textContent = mainPrice.textContent;
        // Update popup image
        const popupImg = popup.querySelector("[data-popup-image]");
        if (popupImg && mainImg) popupImg.src = mainImg.src;
      };

      // Sync main form options to popup when opening
      const syncMainToPopup = () => {
        if (!popup) return;
        form.querySelectorAll('input[type="radio"]:checked').forEach((mainInput) => {
          const val = mainInput.value;
          popup.querySelectorAll("[data-popup-option]").forEach((popupInput) => {
            if (popupInput.value === val) {
              popupInput.checked = true;
              popupInput.closest(".popup-option-pill")?.classList.add("is-active");
            } else if (popupInput.name === "popup-options[" + mainInput.name.replace("options[", "").replace("]", "") + "]") {
              popupInput.closest(".popup-option-pill")?.classList.remove("is-active");
            }
          });
        });
        const priceEl = popup.querySelector("[data-popup-price]");
        const mainPrice = sectionRoot.querySelector("[data-product-price]");
        if (priceEl && mainPrice) priceEl.textContent = mainPrice.textContent;
      };

      // Listen for popup option changes
      if (popup) {
        popup.querySelectorAll("[data-popup-option]").forEach((input) => {
          input.addEventListener("change", () => {
            popup.querySelectorAll(".popup-option-pill").forEach((pill) => {
              pill.classList.toggle("is-active", pill.querySelector("input")?.checked);
            });
            syncPopupToMain();
          });
        });
      }

      doc.querySelector("[data-open-quick-popup]")?.addEventListener("click", () => {
        if (popup) {
          syncMainToPopup();
          popup.hidden = false;
        }
      });

      doc.querySelectorAll("[data-close-popup]").forEach((element) => {
        element.addEventListener("click", () => {
          if (popup) popup.hidden = true;
        });
      });

      doc.querySelector("[data-popup-add]")?.addEventListener("click", () => {
        syncPopupToMain();

        const mainButton = sectionRoot.querySelector("[data-main-add]");
        if (mainButton?.disabled) return;

        if (popup) popup.hidden = true;

        ajaxAddToCart(mainButton);
      });

      doc.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && popup && !popup.hidden) {
          popup.hidden = true;
        }
      });
    });
  };

  const initQuickAdd = (doc) => {
    doc.addEventListener("click", async (e) => {
      const btn = e.target.closest("[data-quick-add]");
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.getAttribute("data-quick-add");
      if (!variantId || btn.classList.contains("is-loading")) return;

      btn.classList.add("is-loading");
      const svg = btn.querySelector("svg");
      if (svg) svg.style.display = "none";

      try {
        await root.fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 })
        });

        const cartRes = await root.fetch("/cart.js");
        const cart = await cartRes.json();

        if (root.__cartDrawer) {
          root.__cartDrawer.renderCartDrawer(cart);
          root.__cartDrawer.open();
        }
      } catch (_e) { /* silent */ }
      finally {
        btn.classList.remove("is-loading");
        if (svg) svg.style.display = "";
      }
    });
  };

  const initFloatingProductCta = (doc) => {
    const mainButton = doc.querySelector("[data-main-add]");
    const floating = doc.querySelector("[data-floating-cta]");
    if (!mainButton || !floating || typeof root.IntersectionObserver === "undefined") return;

    const observer = new root.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          floating.classList.toggle("is-hidden", entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );

    observer.observe(mainButton);
  };


  const initScrollReveal = (doc) => {
    if (typeof root.IntersectionObserver === "undefined") return;

    const staggerContainers = doc.querySelectorAll(
      ".highlights-grid, .trust-grid, .categories-grid, .testimonials-grid, " +
      ".blog-grid, .instagram-grid__items, .faq-list, .best-products-grid__items, " +
      ".search-grid, .editorial-grid"
    );

    staggerContainers.forEach((grid) => {
      Array.from(grid.children).forEach((child, i) => {
        child.classList.add("reveal");
        child.style.transitionDelay = `${i * 0.1}s`;
      });
    });

    const singles = doc.querySelectorAll(
      ".section-head, .section-head--stacked, " +
      ".product-description, " +
      ".carousel-shell, .feature-split, .newsletter-strip .container, " +
      ".collection-hero__content, .hotspot-container, " +
      ".article-body, .article-sidebar, .page-shell .container, " +
      ".cart-items, .cart-summary"
    );

    singles.forEach((el) => el.classList.add("reveal"));

    const observer = new root.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );

    doc.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
  };

  const init = (doc) => {
    initHeader(doc);
    initAnnouncementBar(doc);
    initHero(doc);
    initCountdown(doc);
    initHotspots(doc);
    initCarousels(doc);
    initFaqAccordion(doc);
    initInstagramFeed(doc);
    initSelectDefaults(doc);
    initProductForms(doc);
    initFloatingProductCta(doc);
    initQuickAdd(doc);
    initSearchDrawer(doc);
    initCartDrawer(doc);
    initScrollReveal(doc);
  };

  return {
    findMatchingVariant,
    formatMoney,
    init
  };
});
